import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./DailyWordStyles.css";
import { FaRedo, FaVolumeUp } from "react-icons/fa";
import { useLanguage } from "../../Authentication/LanguageContext";
import {
  DailyWordItem,
  getDailyWordsImageUrl,
  useDailyWordsData,
} from "./DownloadDailyWords";

type DailyWordsProps = {
  compact?: boolean;
};

type SupportedLanguageKey =
  | "german"
  | "english"
  | "tamil"
  | "sinhala"
  | "hindi"
  | "arabic"
  | "turkish"
  | "russian"
  | "chinese"
  | "french"
  | "spanish";

const DEFAULT_DAILY_WORD: DailyWordItem = {
  id: 1,

  headergerman: "Wort des Tages",
  headerenglish: "Daily Word",
  headertamil: "தினசரி வார்த்தை",
  headersinhala: "දෛනික වචනය",
  headerhindi: "दैनिक शब्द",
  headerarabic: "كلمة اليوم",
  headerturkish: "Günlük Kelime",
  headerrussian: "Слово дня",
  headerchinese: "每日单词",
  headerfrench: "Mot du jour",
  headerspanish: "Palabra del día",

  subheadergerman: "",
  subheaderenglish: "",
  subheadertamil: "",
  subheadersinhala: "",
  subheaderhindi: "",
  subheaderarabic: "",
  subheaderturkish: "",
  subheaderrussian: "",
  subheaderchinese: "",
  subheaderfrench: "",
  subheaderspanish: "",

  wordgerman: "Hallo",
  wordenglish: "Hello",
  wordtamil: "வணக்கம்",
  wordsinhala: "ආයුබෝවන්",
  wordhindi: "नमस्ते",
  wordarabic: "مرحبا",
  wordturkish: "Merhaba",
  wordrussian: "Привет",
  wordchinese: "你好",
  wordfrench: "Bonjour",
  wordspanish: "Hola",

  sentencegerman: "Hallo, wie geht es dir heute?",
  sentenceenglish: "Hello, how are you today?",
  sentencetamil: "வணக்கம், இன்று நீங்கள் எப்படி இருக்கிறீர்கள்?",
  sentencesinhala: "ආයුබෝවන්, ඔබ අද කොහොමද?",
  sentencehindi: "नमस्ते, आज आप कैसे हैं?",
  sentencearabic: "مرحبا، كيف حالك اليوم؟",
  sentenceturkish: "Merhaba, heute nasılsın?",
  sentencerussian: "Привет, как дела сегодня?",
  sentencechinese: "你好，你今天怎么样？",
  sentencefrench: "Bonjour, comment allez-vous aujourd'hui ?",
  sentencespanish: "¡Hola! ¿Cómo estás hoy?",

  image: null,
};

const LANGUAGE_KEY_MAP: Record<string, SupportedLanguageKey> = {
  de: "german",
  german: "german",

  en: "english",
  english: "english",

  ta: "tamil",
  tamil: "tamil",

  si: "sinhala",
  sinhala: "sinhala",

  hi: "hindi",
  hindi: "hindi",

  ar: "arabic",
  arabic: "arabic",

  tr: "turkish",
  turkish: "turkish",

  ru: "russian",
  russian: "russian",

  zh: "chinese",
  chinese: "chinese",

  fr: "french",
  french: "french",

  es: "spanish",
  spanish: "spanish",
};

const SPEECH_LANGUAGE_MAP: Record<SupportedLanguageKey, string> = {
  german: "de-DE",
  english: "en-US",
  tamil: "ta-IN",
  sinhala: "si-LK",
  hindi: "hi-IN",
  arabic: "ar-SA",
  turkish: "tr-TR",
  russian: "ru-RU",
  chinese: "zh-CN",
  french: "fr-FR",
  spanish: "es-ES",
};

const getFormattedDate = (): string => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0");

  return `${day}/${month}/${today.getFullYear()}`;
};

const getLanguageKey = (language: string | undefined): SupportedLanguageKey => {
  if (!language) {
    return "english";
  }

  return LANGUAGE_KEY_MAP[language.toLowerCase()] ?? "english";
};

const getStringField = (
  data: DailyWordItem | null,
  fieldName: keyof DailyWordItem
): string => {
  const value = data?.[fieldName];

  return typeof value === "string" ? value : "";
};

const DailyWords: React.FC<DailyWordsProps> = ({ compact = false }) => {
  const { language } = useLanguage();

  const {
    dailyWord,
    loading,
    error,
    initialized,
    retryDailyWord,
    refreshDailyWord,
  } = useDailyWordsData();

  const [currentlyPlaying, setCurrentlyPlaying] = useState<
    "word" | "sentence" | null
  >(null);

  const [isOnline, setIsOnline] = useState<boolean>(() => {
    return typeof navigator === "undefined" ? true : navigator.onLine;
  });

  const selectedLanguageKey = useMemo<SupportedLanguageKey>(() => {
    return getLanguageKey(language);
  }, [language]);

  const todayDate = useMemo(() => getFormattedDate(), []);

  const data = dailyWord ?? DEFAULT_DAILY_WORD;

  const headerGerman = useMemo(() => {
    return getStringField(data, "headergerman") || "Wort des Tages";
  }, [data]);

  const mainWord = useMemo(() => {
    return getStringField(data, "wordgerman") || DEFAULT_DAILY_WORD.wordgerman;
  }, [data]);

  const translatedWord = useMemo(() => {
    if (selectedLanguageKey === "german") {
      return "";
    }

    return getStringField(data, `word${selectedLanguageKey}` as keyof DailyWordItem);
  }, [data, selectedLanguageKey]);

  const mainSentence = useMemo(() => {
    return (
      getStringField(data, "sentencegerman") ||
      DEFAULT_DAILY_WORD.sentencegerman
    );
  }, [data]);

  const translatedSentence = useMemo(() => {
    if (selectedLanguageKey === "german") {
      return "";
    }

    return getStringField(
      data,
      `sentence${selectedLanguageKey}` as keyof DailyWordItem
    );
  }, [data, selectedLanguageKey]);

  const imageUrl = useMemo(() => {
    return getDailyWordsImageUrl(data.image);
  }, [data.image]);

  useEffect(() => {
    const handleOnline = (): void => setIsOnline(true);
    const handleOffline = (): void => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const speakText = useCallback(
    (text: string, type: "word" | "sentence"): void => {
      if (!text || typeof window === "undefined") {
        return;
      }

      if (!("speechSynthesis" in window)) {
        return;
      }

      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = SPEECH_LANGUAGE_MAP.german;
      utterance.rate = 0.9;
      utterance.pitch = 1;

      utterance.onend = () => {
        setCurrentlyPlaying(null);
      };

      utterance.onerror = () => {
        setCurrentlyPlaying(null);
      };

      setCurrentlyPlaying(type);
      window.speechSynthesis.speak(utterance);
    },
    []
  );

  const handleSpeakWord = useCallback(() => {
    speakText(mainWord, "word");
  }, [mainWord, speakText]);

  const handleSpeakSentence = useCallback(() => {
    speakText(mainSentence, "sentence");
  }, [mainSentence, speakText]);

  const showErrorOnly = initialized && !loading && !dailyWord && Boolean(error);

  return (
    <section
      className={`daily-word-card ${compact ? "daily-word-card-compact" : ""}`}
      aria-label="Daily German word"
    >
      <div className="daily-word-header">
        <div className="daily-word-header-left">
          <span className="daily-word-header-title">{headerGerman}</span>
        </div>

        <div className="daily-word-date-badge">{todayDate}</div>
      </div>

      <div className="daily-word-panel">
        <div className="daily-word-panel-base" aria-hidden="true" />
        <div className="daily-word-panel-gloss" aria-hidden="true" />
        <div className="daily-word-accent daily-word-accent-top" aria-hidden="true" />
        <div
          className="daily-word-accent daily-word-accent-bottom"
          aria-hidden="true"
        />
        <div className="daily-word-ring daily-word-ring-one" aria-hidden="true" />
        <div className="daily-word-ring daily-word-ring-two" aria-hidden="true" />

        {showErrorOnly ? (
          <div className="daily-word-error">
            <strong>DailyWords konnten nicht geladen werden.</strong>
            <small>{error}</small>

            <button type="button" onClick={retryDailyWord}>
              <FaRedo />
              Erneut versuchen
            </button>
          </div>
        ) : (
          <>
            {imageUrl && isOnline ? (
              <img
                src={imageUrl}
                alt={mainWord}
                className="daily-word-image"
                loading="lazy"
              />
            ) : null}

            <div className="daily-word-content">
              <button
                type="button"
                className={`daily-word-section daily-word-word-section ${
                  currentlyPlaying === "word" ? "is-playing" : ""
                } ${loading ? "is-fetching" : ""}`}
                onClick={handleSpeakWord}
                onDoubleClick={() => void refreshDailyWord()}
                aria-label={`Speak word ${mainWord}`}
                title="Double click to refresh DailyWord"
              >
                <span className="daily-word-section-label">
                  <FaVolumeUp />
                  Wort
                </span>

                <strong className="daily-word-main-word">{mainWord}</strong>

                {translatedWord ? (
                  <span className="daily-word-translation">
                    {translatedWord}
                  </span>
                ) : null}
              </button>

              <button
                type="button"
                className={`daily-word-section daily-word-sentence-section ${
                  currentlyPlaying === "sentence" ? "is-playing" : ""
                } ${loading ? "is-fetching" : ""}`}
                onClick={handleSpeakSentence}
                onDoubleClick={() => void refreshDailyWord()}
                aria-label="Speak German sentence"
                title="Double click to refresh DailyWord"
              >
                <span className="daily-word-section-label">
                  <FaVolumeUp />
                  Satz
                </span>

                <strong className="daily-word-main-sentence">
                  {mainSentence}
                </strong>

                {translatedSentence ? (
                  <span className="daily-word-translation daily-word-sentence-translation">
                    {translatedSentence}
                  </span>
                ) : null}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default DailyWords;